import { CharacterHelpers, CharacterInitializer, CharacterItemSearchResult, CharacterItemSuite, LocalCharacterInitializer } from "../../../../../entities/characters/Character";
import { ItemHelpers } from "../../../../../entities/library/items/Item";
import { OwnedModReference } from "../../../../../entities/Ownership";
import { nonEmpty } from "../../../../../utils/TypeUtils";
import { CharacterSaveItem, CharacterAddItem, CharacterUpdateItemName, CharacterUpdateItemNotes, CharacterMoveItems, MoveItemGroupOperation, CharacterRemoveItems, CharacterAssignMod, CharacterAssignBodyMod, CharacterRemoveMod, CharacterRemoveBodyMod, CharacterAddSlots, CharacterRemoveSlots, CharacterUpdateSlots } from "../actions/CharacterItemActions";
import { CharacterState } from "../CharacterStore.State";
import { CharacterReducerHelpers } from "./CharacterReducerHelpers";

const saveItem = (state: CharacterState, action: CharacterSaveItem): CharacterState => {
    const { characterId, newInstanceId, oldInstanceId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const result = CharacterHelpers.searchItemByInstanceId(character, oldInstanceId);
    if (result == null) {
        return state;
    }

    const { collectionName, reference } = result;

    const pendingProperties = { ...character.pendingProperties };
    delete pendingProperties[reference.instanceId];

    const copy = ItemHelpers.deepCopy(reference);
    copy.instanceId = newInstanceId;

    const newCharacter = {
        ...character,
        pendingProperties,
        ...CharacterReducerHelpers.getItemCollectionWithReplacement(character, collectionName, reference.instanceId, copy)
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const addItem = (state: CharacterState, action: CharacterAddItem): CharacterState => {
    const { characterId, item } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = {
        ...character,
        loose: [...character.loose, item]
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateItemName = (state: CharacterState, action: CharacterUpdateItemName): CharacterState => {
    const { characterId, itemInstanceId, customName } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const result = CharacterHelpers.searchItemByInstanceId(character, itemInstanceId);
    if (result == null) {
        console.error('Attempt to update item name on character it was not assigned to.');
        return state;
    }

    const { collectionName, reference } = result;    
    const copy = {
        ...reference,
        customName
    };

    const newCharacter = {
        ...character,
        ...CharacterReducerHelpers.getItemCollectionWithReplacement(character, collectionName, reference.instanceId, copy)
    }

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateItemNotes = (state: CharacterState, action: CharacterUpdateItemNotes): CharacterState => {
    const { characterId, itemInstanceId, customNotes } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const result = CharacterHelpers.searchItemByInstanceId(character, itemInstanceId);
    if (result == null) {
        console.error('Attempt to update item notes on character it was not assigned to.');
        return state;
    }

    const { collectionName, reference } = result;    
    const copy = {
        ...reference,
        customNotes
    };

    const newCharacter = {
        ...character,
        ...CharacterReducerHelpers.getItemCollectionWithReplacement(character, collectionName, reference.instanceId, copy)
    }

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const moveItems = (state: CharacterState, action: CharacterMoveItems): CharacterState => {
    const { characterId, operationOne, operationTwo } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const groupOneInstanceIds = operationOne.group.map(i => i.instanceId);
    const groupTwoInstanceIds = operationTwo?.group.map(i => i.instanceId) ?? [];
    const [suite] = CharacterHelpers.removeItems(character, [...groupOneInstanceIds, ...groupTwoInstanceIds]);

    let newCharacter = { ...character, ...suite };

    newCharacter = { ...newCharacter, ...performMove(newCharacter, action.operationOne) };
    if (action.operationTwo) {
        newCharacter = { ...newCharacter, ...performMove(newCharacter, action.operationTwo) };
    }

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const performMove = (character: CharacterInitializer, operation: MoveItemGroupOperation): CharacterItemSuite => {
    const { collection, group } =  operation;
    switch (collection) {
        case 'held':
            return { held: [...character.held, ...group] };
        case 'worn':
            return { worn: [...character.worn, ...group] };
        case 'loose':
            return { loose: [...character.loose, ...group] };
        case 'activeMods':
            return { activeMods: [...character.activeMods, ...group] };
        case 'bodyMods':
            return { bodyMods: [...character.bodyMods, ...group] };
    }
}

const removeItems = (state: CharacterState, action: CharacterRemoveItems): CharacterState => {
    const { characterId, instanceIds } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const [suite,removed] = CharacterHelpers.removeItems(character, instanceIds);
    const newLoose = suite.loose ?? [];
    const newCharacter = { ...character, loose: [...newLoose, ...removed] };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const assignMod = (state: CharacterState, action: CharacterAssignMod): CharacterState => {
    const { characterId, itemInstanceId, modInstanceId, slotId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const item = CharacterHelpers.searchItemByInstanceId(character, itemInstanceId);
    if (item == null) {
        console.error('Attempt to assign mod to unknown item.');
        return state;
    }

    const modSearchResult = CharacterHelpers.searchItemByInstanceId(character, modInstanceId);
    if (modSearchResult == null) {
        console.error('Attempt to assign unknown mod to item.');
        return state;
    }

    const { reference: mod } = modSearchResult;
    
    const newCharacter: LocalCharacterInitializer = {
        ...character,
        loose: character.loose.filter(m => m.instanceId != modInstanceId),
        activeMods: [...character.activeMods, {
            ...mod,
            assignedSlotId: slotId,
            assignedItemId: itemInstanceId,
        }]
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const assignBodyMod = (state: CharacterState, action: CharacterAssignBodyMod): CharacterState => {
    const { characterId, modInstanceId, slotId } = action;
    const character = state.characters.find(c => c.id == characterId);
    if (character == null) {
        throw "Attempted to assign body mod to nonexistent character.";
    }
    
    const mod = CharacterHelpers.searchItemByInstanceId(character, modInstanceId) as OwnedModReference | null;
    if (mod == null) {
        console.error("Attempt to assign unknown body mod.");
        return state;
    }

    const newCharacter: LocalCharacterInitializer = {
        ...character,
        loose: character.loose.filter(m => m.instanceId != modInstanceId),
        bodyMods: [...character.activeMods, {
            ...mod,
            assignedSlotId: slotId,
            assignedItemId: undefined,
        }]
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const removeMod = (state: CharacterState, action: CharacterRemoveMod): CharacterState => {
    const { characterId, itemInstanceId, slotId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const mod = character.activeMods.find(m => m.assignedSlotId == slotId);
    if (mod == null) {
        console.error("Attempted to remove mod from an unassigned slot.");
        return state;
    }

    const newMod = {
        ...mod,
        assignedSlotId: undefined,
        assignedItemId: undefined,
    };

    const newCharacter: LocalCharacterInitializer = {
        ...character,
        activeMods: character.activeMods.filter(m => m.assignedSlotId != slotId),
        loose: [...character.loose, newMod]
    };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const removeBodyMod = (state: CharacterState, action: CharacterRemoveBodyMod): CharacterState => {
    const { characterId, slotId } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const mod = character.bodyMods.find(m => m.assignedSlotId == slotId);
    if (mod == null) {
        console.error("Attempted to remove body mod from an unassigned slot.");
        return state;
    }

    const newMod = {
        ...mod,
        assignedSlotId: undefined,
    }

    const newCharacter: LocalCharacterInitializer = {
        ...character,
        bodyMods: character.bodyMods.filter(m => m.assignedSlotId != slotId),
        loose: [...character.loose, newMod],
    }

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const addSlots = (state: CharacterState, action: CharacterAddSlots): CharacterState => {
    const { characterId, requests } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const itemSearchResults = requests.map(request => CharacterHelpers.searchItemByInstanceId(character, request.itemInstanceId))
        .filter(nonEmpty);

    const itemUpdateSuite = itemSearchResults.reduce((accum: CharacterItemSuite, curr: CharacterItemSearchResult) => {
        const { collectionName, reference } = curr;
        const request = requests.find(r => r.itemInstanceId == reference.instanceId);
        if (request == null) {
            return accum;
        }

        if (accum[collectionName] == null) {
            accum[collectionName] = character[collectionName];
        }
        accum[collectionName] = [
            ...accum[collectionName].filter(i => i.instanceId != reference.instanceId),
            { ...reference, customSlots: [...reference.customSlots, ...request.slots] }
        ];
        return accum;
    }, {});

    const newCharacter = { ...character, ...itemUpdateSuite };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const removeSlots = (state: CharacterState, action: CharacterRemoveSlots): CharacterState => {
    const { characterId, itemInstanceIds, slotIds } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const itemSearchResults = itemInstanceIds.map(instanceId => CharacterHelpers.searchItemByInstanceId(character, instanceId))
        .filter(nonEmpty);

    let itemUpdateSuite = itemSearchResults.reduce((accum: CharacterItemSuite, curr: CharacterItemSearchResult) => {
        const { collectionName, reference } = curr;
        if (accum[collectionName] == null) {
            accum[collectionName] = character[collectionName];
        }
        accum[collectionName] = [
            ...accum[collectionName].filter(i => i.instanceId != reference.instanceId),
            { ...reference, customSlots: reference.customSlots.filter(cs => !slotIds.any(sid => cs.id == sid))}
        ];
        return accum;
    }, {});

    let removedModsSuite = character.activeMods.filter(m => slotIds.any(sid => sid == m.assignedSlotId))
        .map(m => ({ ...m, assignedItemId: undefined, assignedSlotId: undefined }));
    const loose = itemUpdateSuite.loose ?? character.loose;
    itemUpdateSuite = {
        ...itemUpdateSuite,
        loose: [...loose, ...removedModsSuite]
    }

    const newCharacter = { ...character, ...itemUpdateSuite };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateSlots = (state: CharacterState, action: CharacterUpdateSlots): CharacterState => {
    const { characterId, itemInstanceIds, updateIdResponses } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const itemSearchResults = itemInstanceIds.map(instanceId => CharacterHelpers.searchItemByInstanceId(character, instanceId))
        .filter(nonEmpty);

    const itemUpdateSuite = itemSearchResults.reduce((accum: CharacterItemSuite, curr: CharacterItemSearchResult) => {
        const { collectionName, reference } = curr;
        if (accum[collectionName] == null) {
            accum[collectionName] = character[collectionName];
        }

        const newSlots = reference.customSlots.map(cs => {
            const idResponse = updateIdResponses.find(({ oldId }) => cs.id == oldId);
            if (idResponse == null) {
                return null;
            }
            const { newId } = idResponse;
            return ({ ...cs, id: newId });
        }).filter(nonEmpty);

        accum[collectionName] = [
            ...accum[collectionName].filter(i => i.instanceId != reference.instanceId),
            { ...reference, customSlots: newSlots }
        ]

        return accum;
    }, {})

    const newCharacter = { ...character, ...itemUpdateSuite };
    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

export const CharacterItemReducers = {
    saveItem,
    addItem,
    updateItemName,
    updateItemNotes,
    moveItems,
    removeItems,
    assignMod,
    assignBodyMod,
    removeMod,
    removeBodyMod,
    addSlots,
    removeSlots,
    updateSlots,
}